"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Top-level error boundary. Logs the full React component stack to the
// browser console so that minified-prod errors like #185 (Maximum update
// depth exceeded) can be traced back to the actual component name. The
// componentStack field is the part Next.js's default error UI strips —
// without it, prod stacks only show obfuscated function names.
export class TopLevelErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      "[ErrorBoundary] caught:",
      error,
      "\ncomponentStack:",
      info.componentStack,
    );
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-black p-8 text-white">
          <div className="max-w-md space-y-3 text-center">
            <p className="text-lg font-semibold">Something went wrong</p>
            <p className="text-sm text-white/70">
              Open the browser console to see which component crashed, then
              refresh.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded border border-white/30 px-4 py-1.5 text-sm hover:bg-white/10"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
